/**
 * ScenarioPreview — hover / long-press popup card
 *
 * Rendered into document.body via a portal so it is never clipped
 * by overflow:hidden ancestors in the instructor panel layout.
 *
 * Position logic:
 *   – Prefers the side of the screen with more room (right → left fallback).
 *   – Vertically centres on the anchor button, clamped to viewport.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SCENARIOS } from '../../engine/scenarioEngine';
import { MODIFIERS, INTERVENTIONS } from '../../engine/interventionEngine';
import styles from './ScenarioPreview.module.css';

// ── Vital display rows shown in the popup ────────────────────────────────────

const VITAL_CELLS = [
  { key: 'HR',       label: 'HR',    unit: 'bpm',  fmt: v  => Math.round(v) },
  { key: 'SpO2',     label: 'SpO₂',  unit: '%',    fmt: v  => Math.round(v) },
  { key: 'EtCO2',    label: 'EtCO₂', unit: 'mmHg', fmt: v  => Math.round(v) },
  { key: 'NIBP',     label: 'NIBP',  unit: 'mmHg', fmt: (_, vitals) => `${Math.round(vitals.NIBP_Sys)}/${Math.round(vitals.NIBP_Dia)}`, statusKey: 'NIBP_Sys' },
  { key: 'RR',       label: 'RR',    unit: '/min', fmt: v  => Math.round(v) },
  { key: 'Temp',     label: 'Temp',  unit: '°C',   fmt: v  => v?.toFixed(1) },
  { key: 'CVP',      label: 'CVP',   unit: 'mmHg', fmt: v  => Math.round(v) },
];

// Clinical thresholds for colour coding: [warn_low, warn_high, crit_low, crit_high]
const THRESHOLDS = {
  HR:       [50, 110, 35, 150],
  SpO2:     [94, 100, 88, 100],
  NIBP_Sys: [85, 155, 70, 175],
  EtCO2:    [30, 48,  18, 62 ],
  RR:       [10, 22,  5,  30 ],
  Temp:     [35.5, 38.0, 34.0, 39.5],
  CVP:      [1,  10,  0,   14 ],
};

function vitalStatus(key, value) {
  if (value === 0 && (key === 'HR' || key === 'SpO2' || key === 'NIBP_Sys')) return 'crisis';
  const t = THRESHOLDS[key];
  if (!t) return 'ok';
  const [wl, wh, cl, ch] = t;
  if (value < cl || value > ch) return 'crisis';
  if (value < wl || value > wh) return 'warn';
  return 'ok';
}

// ── Delta display for modifiers ──────────────────────────────────────────────

const DELTA_CELLS = [
  { key: 'HR',       label: 'HR',    unit: 'bpm'  },
  { key: 'SpO2',     label: 'SpO₂',  unit: '%'    },
  { key: 'EtCO2',    label: 'EtCO₂', unit: 'mmHg' },
  { key: 'NIBP_Sys', label: 'BP',    unit: 'mmHg', paired: 'NIBP_Dia', fmt: (d) => `${d.NIBP_Sys > 0 ? '+' : ''}${d.NIBP_Sys}/${d.NIBP_Dia > 0 ? '+' : ''}${d.NIBP_Dia}` },
  { key: 'CVP',      label: 'CVP',   unit: 'mmHg' },
];

function fmtDelta(v) {
  if (v === undefined || v === null) return null;
  return (v > 0 ? '+' : '') + v;
}

/**
 * The interventions with a tailored response for this item, for the preview's
 * "Correct interventions sought" list — shown on EVERY scenario card:
 *   • custom scenario → its own `interventions` map
 *   • built-in scenario with an explicit `correctInterventions` list → that list
 *     (so contraindicated drugs authored to demonstrate harm are NOT surfaced)
 *   • built-in scenario otherwise → interventions whose `scenarioDeltas` name it
 *   • modifier → interventions whose `modifierDeltas` name this key
 * Returns [{ key, deltas }].
 */
function correctInterventions(itemKey, type, def) {
  if (type === 'custom') {
    return Object.entries(def.interventions || {}).map(([key, deltas]) => ({ key, deltas }));
  }
  if (type !== 'modifier' && Array.isArray(def.correctInterventions)) {
    // A key may be a drug (INTERVENTIONS) OR a procedure/shock (SCENARIOS, e.g.
    // synchronizedCardioversion / defibrillation) — the latter shows as a labelled
    // action with no vital-delta arrows.
    return def.correctInterventions
      .filter((key) => INTERVENTIONS[key] || SCENARIOS[key])
      .map((key) => ({
        key,
        deltas: INTERVENTIONS[key]?.scenarioDeltas?.[itemKey] ?? INTERVENTIONS[key]?.deltas ?? {},
        procedure: !INTERVENTIONS[key],
      }));
  }
  const field = type === 'modifier' ? 'modifierDeltas' : 'scenarioDeltas';
  return Object.entries(INTERVENTIONS)
    .filter(([, iv]) => iv[field]?.[itemKey])
    .map(([key, iv]) => ({ key, deltas: iv[field][itemKey] }));
}

// Compact arrow summary of an intervention's response for this scenario.
function summarizeEffect(d) {
  const arr = (v) => (v > 0 ? '↑' : '↓');
  const parts = [];
  if (d.HR) parts.push(arr(d.HR) + 'HR');
  if (d.NIBP_Sys) parts.push(arr(d.NIBP_Sys) + 'BP');
  if (d.SpO2) parts.push(arr(d.SpO2) + 'SpO₂');
  if (d.EtCO2) parts.push(arr(d.EtCO2) + 'EtCO₂');
  if (d.RR) parts.push(arr(d.RR) + 'RR');
  if (d.Temp) parts.push(arr(d.Temp) + 'T');
  if (d.rhythm) parts.push('→ ' + d.rhythm);
  if (d.vent?.ppeak) parts.push(arr(d.vent.ppeak) + 'Paw');
  return parts.join('  ');
}

// ── Popup content ─────────────────────────────────────────────────────────────

function PreviewContent({ itemKey, type, customScenarios }) {
  const isModifier = type === 'modifier';
  const isCustom = type === 'custom';
  const def = isModifier
    ? MODIFIERS[itemKey]
    : isCustom
      ? customScenarios?.find((c) => c.key === itemKey)
      : SCENARIOS[itemKey];
  if (!def) return null;

  const color = isModifier ? (def.color ?? '#8888aa') : isCustom ? '#0090FF' : '#00E676';

  return (
    <>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.dot} style={{ background: color }} />
        <span className={styles.title}>{def.name}</span>
        {isModifier && <span className={styles.badge}>modifier</span>}
        {isCustom && <span className={styles.badge}>custom</span>}
      </div>

      {/* Description */}
      {def.description && (
        <p className={styles.description}>{def.description}</p>
      )}

      {/* Vitals grid — scenarios only */}
      {!isModifier && def.vitals && (
        <>
          <div className={styles.divider} />
          <div className={styles.vitalsGrid}>
            {VITAL_CELLS.map(({ key, label, unit, fmt, statusKey }) => {
              const resolveKey = statusKey ?? key;
              const rawValue = key === 'NIBP' ? def.vitals.NIBP_Sys : def.vitals[key];
              if (rawValue === undefined) return null;
              const displayVal = fmt
                ? fmt(rawValue, def.vitals)
                : Math.round(rawValue);
              const status = vitalStatus(resolveKey, def.vitals[resolveKey] ?? rawValue);
              return (
                <div key={key} className={styles.vitalCell}>
                  <span className={styles.vitalLabel}>{label}</span>
                  <span className={`${styles.vitalValue} ${styles[status]}`}>
                    {displayVal}
                  </span>
                  <span className={styles.vitalUnit}>{unit}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Correct interventions — shown on every card that has tailored responses */}
      {(() => {
        const ivs = correctInterventions(itemKey, type, def);
        if (ivs.length === 0) return null;
        const shown = ivs.slice(0, 8);
        return (
          <>
            <div className={styles.divider} />
            <div className={styles.deltaLabel}>Correct interventions sought:</div>
            <div className={styles.ivList}>
              {shown.map(({ key, deltas, procedure }) => (
                <div key={key} className={styles.ivRow}>
                  <span className={styles.ivName}>{INTERVENTIONS[key]?.shortName ?? INTERVENTIONS[key]?.name ?? SCENARIOS[key]?.name ?? key}</span>
                  <span className={styles.ivEffect}>{procedure ? '⚡ procedure' : summarizeEffect(deltas)}</span>
                </div>
              ))}
              {ivs.length > shown.length && (
                <div className={styles.ivMore}>+{ivs.length - shown.length} more</div>
              )}
            </div>
          </>
        );
      })()}

      {/* Contraindicated — drugs to avoid for this scenario */}
      {!isModifier && Array.isArray(def.contraindicated) && def.contraindicated.length > 0 && (
        <>
          <div className={styles.divider} />
          <div className={styles.avoidLabel}>Avoid / contraindicated:</div>
          <div className={styles.avoidList}>
            {def.contraindicated
              .filter((key) => INTERVENTIONS[key])
              .map((key) => (
                <span key={key} className={styles.avoidChip}>
                  {INTERVENTIONS[key]?.shortName ?? INTERVENTIONS[key]?.name ?? key}
                </span>
              ))}
          </div>
        </>
      )}

      {/* Delta table — modifiers only */}
      {isModifier && def.deltas && (
        <>
          <div className={styles.divider} />
          <div className={styles.deltaLabel}>Effect when applied:</div>
          <div className={styles.deltaGrid}>
            {DELTA_CELLS.map(({ key, label, unit, paired, fmt: dFmt }) => {
              if (key === 'NIBP_Sys' && !def.deltas.NIBP_Sys) return null;
              const val = def.deltas[key];
              if (val === undefined) return null;
              let display;
              if (dFmt) {
                display = dFmt(def.deltas);
              } else {
                display = fmtDelta(val);
              }
              const isNeg = val < 0;
              return (
                <div key={key} className={styles.deltaCell}>
                  <span className={styles.deltaParamLabel}>{label}</span>
                  <span className={`${styles.deltaValue} ${isNeg ? styles.deltaDown : styles.deltaUp}`}>
                    {display} <span className={styles.deltaUnit}>{unit}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// ── Positioned popup shell ────────────────────────────────────────────────────

const POPUP_W = 252; // px — fixed width for position math

export function ScenarioPreviewPopup({ itemKey, type, customScenarios, anchorRect, onClose, onPopupMouseEnter, onPopupMouseLeave }) {
  const popupRef = useRef(null);
  // Start hidden via opacity so layout is calculated before first paint
  const [pos, setPos] = useState(null);

  // useLayoutEffect fires synchronously after DOM mutation, before the browser
  // paints — ensures the popup is at the correct position on the very first frame
  // (no jump from -9999 placeholder that would prevent mouseenter from firing).
  useLayoutEffect(() => {
    if (!anchorRect || !popupRef.current) return;
    const popH = popupRef.current.offsetHeight || 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const MARGIN = 10;

    // Horizontal: prefer right side, fall back to left
    let left;
    if (anchorRect.right + POPUP_W + MARGIN <= vw) {
      left = anchorRect.right + MARGIN;
    } else if (anchorRect.left - POPUP_W - MARGIN >= 0) {
      left = anchorRect.left - POPUP_W - MARGIN;
    } else {
      left = Math.max(MARGIN, (anchorRect.left + anchorRect.right) / 2 - POPUP_W / 2);
    }

    // Vertical: centre on button, clamp to viewport
    let top = anchorRect.top + anchorRect.height / 2 - popH / 2;
    top = Math.max(MARGIN, Math.min(top, vh - popH - MARGIN));

    setPos({ left, top });
  }, [anchorRect, itemKey]);

  // Close on outside mousedown (non-blocking — doesn't intercept hover events
  // the way a full-screen backdrop div would)
  useEffect(() => {
    const handleOutsideMouseDown = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleOutsideTouchStart = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideMouseDown, true);
    document.addEventListener('touchstart', handleOutsideTouchStart, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown, true);
      document.removeEventListener('touchstart', handleOutsideTouchStart, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={popupRef}
      className={styles.popup}
      style={{
        width: POPUP_W,
        // pos is null on the very first render (before useLayoutEffect fires).
        // Keep it in the DOM for measurement but invisible so the user never
        // sees a flash at (0,0) and the mouse can't accidentally enter it.
        visibility: pos ? 'visible' : 'hidden',
        left: pos ? pos.left : 0,
        top:  pos ? pos.top  : 0,
      }}
      // Keep popup alive while mouse is inside it
      onMouseEnter={onPopupMouseEnter}
      onMouseLeave={onPopupMouseLeave}
    >
      <PreviewContent itemKey={itemKey} type={type} customScenarios={customScenarios} />
    </div>,
    document.body
  );
}
